import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DashboardPageHeader } from '../../Components/Common/DashboardKit';
import api from '../../api';
import './shell/hr-shell.css';

import HrLandingCards from './shell/HrLandingCards';
import HrFtoWorkspace from './shell/HrFtoWorkspace';
import HrCoaWorkspace from './shell/HrCoaWorkspace';
import HrReviewSection from './shell/HrReviewSection';
import HrPunchesSection from './shell/HrPunchesSection';
import HrLeaveSection from './shell/HrLeaveSection';
import HrEmployeesSection from './shell/HrEmployeesSection';
import HrReportsSection from './shell/HrReportsSection';
import HrMoreSection from './shell/HrMoreSection';
import HrEmbeddedPage from './shell/HrEmbeddedPage';
import WebFormsTimelineTabs from './WebFormsTimelineTabs';

const LazyLateInReview  = React.lazy(() => import('./LateInReview'));
const LazyLateInReport  = React.lazy(() => import('./LateInReport'));
const LazyProfiles      = React.lazy(() => import('./ProfileUpdateApprovals'));
const LazyHrDashboard   = React.lazy(() => import('./HrDashboard'));

/**
 * Human Resources — the ONE canonical HR shell.
 * Round HR-PA (HR + Personal Attendance redesign) — Part 2.
 *
 * THE LANDING IS EXACTLY NINE CARDS (the owner's structure, one level,
 * no card-hub-behind-a-card):
 *
 *   Add Employee · Manage Employee · FTO · COA · Reports ·
 *   Punch Editor · Late In & Early Out · Profile Review · Dashboard
 *
 * Each card opens its WORKSPACE (?section=…) with its own small sub-nav
 * and a "Back to Human Resources" action. The previous six-tab bar is
 * gone from the landing — cards ARE the navigation — but every previous
 * section key still renders, so nothing that linked here breaks.
 *
 * URL SCHEME (linkable, refresh-safe, Back-button-safe)
 *   /humanresource                        → the nine cards
 *   /humanresource?section=fto            &tab=add|remove|holidays|approvals|summary|more(&item=…)
 *   /humanresource?section=coa            &tab=add|remove|approvals|history
 *   /humanresource?section=latein         &sub=summary (rollup report)
 *   /humanresource?section=profiles
 *   /humanresource?section=dashboard
 *   /humanresource?section=punches        &mode=editor|missing|pending (&class=…)
 *   /humanresource?section=employees      &view=list|add|reactivate
 *   /humanresource?section=reports        &report=fto-report|coa-report|payroll|punchgrid|salaried|hourly|attendance|analytics
 *   /humanresource?section=review|leave|more|webforms   (kept working)
 *
 * BACKWARD COMPATIBILITY (Part 30 — nothing 404s, nothing dead-ends)
 *   • Old ?tab=1..6 workspace links normalize one-shot (history.replace):
 *       1→employees · 2→reports · 3→fto · 4→punches(missing) ·
 *       5→webforms · 6→review
 *   • ?section=leave&area=fto(&item=…) → section=fto (item mapped to the
 *     matching tab); leave&area=coa → coa; leave&area=history → coa
 *     history. leave itself (requests/record) still renders.
 *   • ?section=review&view=latein → section=latein;
 *     ?section=review&view=profiles → section=profiles;
 *     ?mode=salaried|hourly → mode=missing&class=…;
 *     ?section=reports&report=latein → section=latein.
 *   • Every old deep /hr/* route still redirects into the matching
 *     section (see allRoutes.js) with its original permission wrapper.
 *
 * All embedded pages are the SAME components with their original
 * endpoints, permissions, and validations — this shell is composition.
 */

// Sections that render inside the shell. (The six legacy keys stay valid.)
const VALID_SECTIONS = [
    'fto', 'coa', 'latein', 'profiles', 'dashboard',
    'review', 'punches', 'leave', 'employees', 'reports', 'more', 'webforms',
];

// Legacy ?tab= workspace keys → new sections (old bookmarks / palette).
const LEGACY_TAB_MAP = {
    '1': { section: 'employees' },
    '2': { section: 'reports' },
    '3': { section: 'fto' },
    '4': { section: 'punches', mode: 'missing' },
    '5': { section: 'webforms' },
    '6': { section: 'review' },
};

// Old Leave→FTO tool keys → FTO workspace tabs (Part 30).
const LEAVE_FTO_ITEM_MAP = {
    'add-fto': { tab: 'add' },
    'holidays': { tab: 'holidays' },
    'summary': { tab: 'summary' },
    'record-paid': { tab: 'add' }, // payouts tool retired 2026-08-18 — lookup lives on Add FTO
    'year-end': { tab: 'more', item: 'year-end' },
    'year-end-request': { tab: 'more', item: 'year-end-request' },
    'comp-days': { tab: 'more', item: 'comp-days' },
};

// Human labels for the breadcrumb's current level.
const SECTION_LABELS = {
    fto: 'FTO', coa: 'COA', latein: 'Late in & early out', profiles: 'Profile review',
    dashboard: 'Dashboard', review: 'Review', punches: 'Punch editor', leave: 'Leave',
    employees: 'Employees', reports: 'Reports', more: 'More', webforms: 'Web forms',
};
const SUB_LABELS = {
    fto:       { add: 'Add FTO', remove: 'Remove FTO', holidays: 'Holidays', approvals: 'Approvals', summary: 'Summary', more: 'More tools' },
    coa:       { add: 'Add COA', remove: 'Remove COA', approvals: 'Approvals', history: 'History' },
    latein:    { daily: 'Daily review', summary: 'Summary report' },
    review:    { queue: 'Review queue', profiles: 'Profile approvals', latein: 'Late-in & early-out' },
    punches:   { editor: 'Punch editor', missing: 'Missing punches', pending: 'Pending requests' },
    leave:     { requests: 'Requests', record: 'Record absence', fto: 'FTO administration', coa: 'COA administration', history: 'History' },
    employees: { list: 'Employees', add: 'Add employee', reactivate: 'Reactivate' },
    reports:   { 'fto-report': 'FTO reports', 'coa-report': 'COA reports', payroll: 'Payroll time card',
                 punchgrid: 'Punch grid', salaried: 'Salaried', hourly: 'Hourly', attendance: 'Attendance', analytics: 'Analytics' },
};

const HumanResource = () => {
    document.title = 'Human Resources | SSW Technologies Dashboard';

    const [searchParams, setSearchParams] = useSearchParams();

    // ── Legacy ?tab= → ?section= (one-shot URL normalize) ────────────────
    const legacyTab = searchParams.get('tab') && !searchParams.get('section')
        ? searchParams.get('tab') : null;
    const legacy = legacyTab ? LEGACY_TAB_MAP[legacyTab] : null;
    useEffect(() => {
        if (!legacy) return;
        const next = { section: legacy.section };
        if (legacy.mode) next.mode = legacy.mode;
        setSearchParams(next, { replace: true });
    }, [legacyTab]);

    // Effective values for THIS render (legacy links paint correctly even
    // before the replace effect lands — no blank/flash frame).
    const rawSection = legacy ? legacy.section : searchParams.get('section');
    let section = VALID_SECTIONS.includes(rawSection) ? rawSection : null; // null → landing

    let subView  = searchParams.get('view');
    const rawMode  = legacy?.mode || searchParams.get('mode');
    let area       = searchParams.get('area');
    let item       = searchParams.get('item');
    const report   = searchParams.get('report');
    let subParam   = searchParams.get('sub');
    let tab        = searchParams.get('tab') && searchParams.get('section') ? searchParams.get('tab') : null;

    // ── One-shot normalizations (same replace pattern as ?tab=, with the
    //    EFFECTIVE values computed for this render — no flash, no loop) ──
    //
    // a) ?mode=salaried|hourly (old pills) → mode=missing&class=…
    const legacyPunchClass = rawMode === 'salaried' || rawMode === 'hourly' ? rawMode : null;
    const mode = legacyPunchClass ? 'missing' : rawMode;
    const punchClass = searchParams.get('class') || legacyPunchClass || 'salaried';
    useEffect(() => {
        if (section === 'punches' && legacyPunchClass) {
            setSearchParams({ section: 'punches', mode: 'missing', class: legacyPunchClass }, { replace: true });
        }
    }, [section, legacyPunchClass]);
    //
    // b) reports&report=latein → the ONE late-in home (its own workspace now).
    const lateinForward = section === 'reports' && report === 'latein';
    if (lateinForward) { section = 'latein'; }
    useEffect(() => {
        if (lateinForward) setSearchParams({ section: 'latein' }, { replace: true });
    }, [lateinForward]);
    //
    // c) review&view=latein / review&view=profiles → their workspaces
    //    (the cards are the front door now; review keeps the queue).
    const reviewForward = section === 'review' && (subView === 'latein' || subView === 'profiles');
    let reviewForwardTo = null;
    if (reviewForward) {
        reviewForwardTo = subView === 'latein'
            ? { section: 'latein', ...(subParam ? { sub: subParam } : {}) }
            : { section: 'profiles' };
        section = reviewForwardTo.section;
        subParam = reviewForwardTo.sub || subParam;
        subView = null;
    }
    useEffect(() => {
        if (reviewForwardTo) setSearchParams(reviewForwardTo, { replace: true });
    }, [JSON.stringify(reviewForwardTo)]);
    //
    // d) leave&area=fto(&item=…) → FTO workspace; leave&area=coa → COA;
    //    leave&area=history → COA history. (requests/record still render
    //    under leave.)
    let leaveForwardTo = null;
    if (section === 'leave' && (area === 'fto' || area === 'coa' || area === 'history')) {
        if (area === 'fto') {
            const mapped = (item && LEAVE_FTO_ITEM_MAP[item]) || { tab: 'summary' };
            leaveForwardTo = { section: 'fto', tab: mapped.tab, ...(mapped.item ? { item: mapped.item } : {}) };
        } else {
            leaveForwardTo = { section: 'coa', tab: area === 'history' ? 'history' : 'add' };
        }
        section = leaveForwardTo.section;
        tab = leaveForwardTo.tab;
        item = leaveForwardTo.item || null;
    }
    useEffect(() => {
        if (leaveForwardTo) setSearchParams(leaveForwardTo, { replace: true });
    }, [JSON.stringify(leaveForwardTo)]);

    // Param setters — history PUSH so browser Back walks the user's path.
    const setSub = (extra) => setSearchParams({ section, ...extra });

    // ── Audit-trail allowlist (same server check as before) — fetched
    // lazily, only once More is opened. Fail-silent hides the card.
    const [auditAllowed, setAuditAllowed] = useState(false);
    const [auditChecked, setAuditChecked] = useState(false);
    useEffect(() => {
        if (section !== 'more' || auditChecked) return;
        setAuditChecked(true);
        api.get('audit/access')
            .then((r) => setAuditAllowed(!!r.data?.allowed))
            .catch(() => setAuditAllowed(false));
    }, [section, auditChecked]);

    // ── Breadcrumb: Human Resource (link home) › current level ──────────
    let levelLabel = null;
    if (section) {
        const base = SECTION_LABELS[section] || section;
        const subKey =
            section === 'fto'       ? (tab || 'add') :
            section === 'coa'       ? (tab || 'add') :
            section === 'latein'    ? (subParam === 'summary' ? 'summary' : 'daily') :
            section === 'review'    ? (subView || 'queue') :
            section === 'punches'   ? (mode || 'editor') :
            section === 'leave'     ? (area || 'requests') :
            section === 'employees' ? (subView || 'list') :
            section === 'reports'   ? (report || 'fto-report') : null;
        const subLabel = subKey ? SUB_LABELS[section]?.[subKey] : null;
        levelLabel = subLabel && subLabel !== base ? `${base} · ${subLabel}` : base;
        if (section === 'more' && item === 'audit') levelLabel = 'More · Audit trail';
    }
    const breadcrumb = levelLabel
        ? [{ label: 'Human Resource', to: '/humanresource' }, { label: levelLabel }]
        : [{ label: 'Human Resource' }];

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content" style={{ background: 'var(--ssw-surface-2, #f5f5f5)', minHeight: '100vh' }}>
                <Container fluid>
                    {/* ONE compact header for the whole HR area — the giant
                        per-subpage banners are suppressed inside embeds. */}
                    <DashboardPageHeader
                        compact
                        icon="ri-team-line"
                        title="Human Resources"
                        subtitle={section ? undefined : 'Pick a task — every tool is one card away.'}
                        tone="violet"
                        breadcrumb={breadcrumb}
                    />

                    {/* Inside a workspace: one consistent way back (Part 2 —
                        cards are the navigation; no tab bar duplication). */}
                    {section && (
                        <div className="mb-2">
                            <Link to="/humanresource" className="btn btn-light btn-sm">
                                <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />
                                Back to Human Resources
                            </Link>
                        </div>
                    )}

                    <div className="hr-shell-card">
                        {!section && <HrLandingCards />}

                        {section === 'fto' && (
                            <HrFtoWorkspace
                                tab={tab}
                                moreItem={item}
                                onTabChange={(t) => setSub({ tab: t })}
                                onMoreItemChange={(i) => setSub({ tab: 'more', item: i })}
                            />
                        )}

                        {section === 'coa' && (
                            <HrCoaWorkspace
                                tab={tab}
                                onTabChange={(t) => setSub({ tab: t })}
                            />
                        )}

                        {section === 'latein' && (
                            <div>
                                <div className="hr-section-title">
                                    <div>
                                        <h5>Late in &amp; early out</h5>
                                        <span className="text-muted">Daily exceptions to review, plus the rollup summary report.</span>
                                    </div>
                                    <div className="btn-group btn-group-sm" role="group" aria-label="Late-in views">
                                        <button type="button"
                                            className={`btn ${subParam !== 'summary' ? 'btn-primary' : 'btn-soft-primary'}`}
                                            onClick={() => setSub({})}>
                                            Daily review
                                        </button>
                                        <button type="button"
                                            className={`btn ${subParam === 'summary' ? 'btn-primary' : 'btn-soft-primary'}`}
                                            onClick={() => setSub({ sub: 'summary' })}>
                                            Summary report
                                        </button>
                                    </div>
                                </div>
                                <HrEmbeddedPage>
                                    {subParam === 'summary' ? <LazyLateInReport /> : <LazyLateInReview />}
                                </HrEmbeddedPage>
                            </div>
                        )}

                        {section === 'profiles' && (
                            <div>
                                <div className="hr-section-title">
                                    <div>
                                        <h5>Profile review</h5>
                                        <span className="text-muted">Approve or deny employee profile changes.</span>
                                    </div>
                                </div>
                                <HrEmbeddedPage><LazyProfiles /></HrEmbeddedPage>
                            </div>
                        )}

                        {section === 'dashboard' && (
                            <HrEmbeddedPage><LazyHrDashboard /></HrEmbeddedPage>
                        )}

                        {section === 'review' && (
                            <HrReviewSection
                                view={subView}
                                lateinSub={subParam}
                                onViewChange={(v) => setSub({ view: v })}
                                onLateinSubChange={(s) => setSub({ view: 'latein', sub: s })}
                            />
                        )}

                        {section === 'punches' && (
                            <HrPunchesSection
                                mode={mode}
                                punchClass={punchClass}
                                onModeChange={(m) => setSub({ mode: m })}
                                onClassChange={(c) => setSub({ mode: 'missing', class: c })}
                            />
                        )}

                        {section === 'leave' && (
                            <HrLeaveSection
                                area={area}
                                item={item}
                                onAreaChange={(a) => setSub({ area: a })}
                                onItemChange={(i) => setSub({ area: 'fto', item: i })}
                            />
                        )}

                        {section === 'employees' && (
                            <HrEmployeesSection
                                view={subView}
                                onViewChange={(v) => setSub({ view: v })}
                            />
                        )}

                        {section === 'reports' && (
                            <HrReportsSection
                                report={report}
                                onReportChange={(r) => setSub({ report: r })}
                            />
                        )}

                        {section === 'more' && (
                            <HrMoreSection
                                item={item}
                                auditAllowed={auditAllowed}
                                onOpenItem={(i) => setSearchParams(i ? { section: 'more', item: i } : { section: 'more' })}
                            />
                        )}

                        {section === 'webforms' && (
                            <div>
                                <div className="hr-section-title">
                                    <div>
                                        <h5>Web forms</h5>
                                        <span className="text-muted">HR021 exempt review · 90-day review · HR020 PIC form.</span>
                                    </div>
                                </div>
                                <div className="hr-embedded">
                                    <WebFormsTimelineTabs />
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default HumanResource;
